// /admin/users — manage office users + admins. Visual port of
// Cambonic_SaaS admin/UsersPage.tsx; keeps GutterInventory's single-
// tenant role model (admin/owner/back_office) and the existing
// invite/resend/patch/delete actions. Self-actions are blocked
// server-side; the drawer hides dangerous actions on your own row.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AppRole } from '@gutterinv/shared';
import {
  getAdminUsers,
  inviteAdminUser,
  resendAdminInvite,
  patchAdminUser,
  deleteAdminUser,
  forceSignOutUser,
  getUserRecentLogins,
  type AdminUser,
  type UserRecentLogin,
} from '../../api/admin.js';
import type { MeResponse } from '../../api/client.js';
import { Drawer } from '../../components/admin/Drawer.js';

const ROLE_LABEL: Record<AppRole, string> = {
  admin: 'Admin',
  owner: 'Owner',
  back_office: 'Office staff',
};

const ROLE_OPTIONS: AppRole[] = ['admin', 'owner', 'back_office'];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  if (day < 365) return `${Math.floor(day / 30)}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

export function UsersPage({ me }: { me: MeResponse }) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: getAdminUsers,
  });

  const users = data?.users ?? [];
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [drawerUserId, setDrawerUserId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!users.length) return [] as AdminUser[];
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q) ||
        ROLE_LABEL[u.role].toLowerCase().includes(q) ||
        u.role.includes(q),
    );
  }, [users, search]);

  const drawerUser = useMemo(
    () => (drawerUserId ? users.find((u) => u.id === drawerUserId) ?? null : null),
    [drawerUserId, users],
  );

  function refetch() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-[rgb(var(--color-bronze))] font-semibold mb-1">
            Users
          </p>
          <h1 className="font-display text-3xl text-[rgb(var(--color-navy))]">
            {users.length ? `${filtered.length} of ${users.length}` : '—'}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-[var(--radius-md)] bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))] text-sm font-semibold hover:bg-[rgb(var(--color-navy)/0.9)] transition-colors"
        >
          + Invite user
        </button>
      </div>

      <div className="mb-6">
        <div className="relative">
          <input
            type="search"
            placeholder="Search by email or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 bg-white/60 border border-[rgb(var(--color-navy)/0.15)] rounded-[var(--radius-md)] text-sm focus:outline-none focus:border-[rgb(var(--color-bronze))] focus:ring-2 focus:ring-[rgb(var(--color-bronze)/0.2)]"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[rgb(var(--color-navy)/0.4)]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {isLoading && !users.length && (
        <p className="text-sm text-[rgb(var(--color-navy)/0.4)] text-center py-12">Loading users…</p>
      )}
      {error && (
        <p className="text-sm text-[rgb(var(--color-severity-critical))] text-center py-12">
          {error instanceof Error ? error.message : 'Failed to load users'}
        </p>
      )}
      {users.length > 0 && filtered.length === 0 && (
        <div className="bg-white/40 border border-[rgb(var(--color-navy)/0.1)] rounded-[var(--radius-lg)] p-12 text-center">
          <p className="text-[rgb(var(--color-navy)/0.6)] text-sm">
            No users match "{search}".
          </p>
        </div>
      )}
      {users.length === 0 && !isLoading && !error && (
        <div className="bg-white/40 border border-[rgb(var(--color-navy)/0.1)] rounded-[var(--radius-lg)] p-12 text-center">
          <p className="text-[rgb(var(--color-navy)/0.7)] mb-4">No users yet.</p>
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-[var(--radius-md)] bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))] text-sm font-semibold hover:bg-[rgb(var(--color-navy)/0.9)]"
          >
            + Invite your first user
          </button>
        </div>
      )}
      {filtered.length > 0 && (
        <div className="bg-white/40 border border-[rgb(var(--color-navy)/0.1)] rounded-[var(--radius-lg)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[rgb(var(--color-navy)/0.05)] border-b border-[rgb(var(--color-navy)/0.1)]">
                <Th>User</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Last sign-in</Th>
                <Th>Created</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-navy)/0.08)]">
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setDrawerUserId(u.id)}
                  className="hover:bg-white/60 cursor-pointer transition-colors"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDrawerUserId(u.id);
                    }
                  }}
                >
                  <Td>
                    <div className="font-medium text-[rgb(var(--color-navy))] truncate">
                      {u.displayName ?? u.email}
                    </div>
                    {u.displayName && (
                      <div className="text-[11px] text-[rgb(var(--color-navy)/0.55)] truncate">
                        {u.email}
                      </div>
                    )}
                    {u.userId === me.userId && (
                      <div className="text-[10px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.4)] mt-0.5">
                        You
                      </div>
                    )}
                  </Td>
                  <Td>
                    <RoleBadge role={u.role} />
                  </Td>
                  <Td>
                    {u.active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[rgb(var(--color-navy)/0.85)]">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Active
                      </span>
                    ) : (
                      <span className="text-xs text-[rgb(var(--color-navy)/0.45)]">Inactive</span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-[rgb(var(--color-navy)/0.6)] text-xs">
                      {formatRelative(u.lastSignInAt)}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-[rgb(var(--color-navy)/0.6)] text-xs">
                      {formatDate(u.createdAt)}
                    </span>
                  </Td>
                  <Td>
                    <span className="text-[rgb(var(--color-navy)/0.3)] text-xs">→</span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserDrawer
        user={drawerUser}
        isSelf={drawerUser?.userId === me.userId}
        onClose={() => setDrawerUserId(null)}
        onChanged={refetch}
      />

      {inviteOpen && (
        <InviteDialog
          onClose={() => setInviteOpen(false)}
          onInvited={() => {
            refetch();
            setInviteOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-[rgb(var(--color-navy)/0.55)] px-4 py-2.5">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function RoleBadge({ role }: { role: AppRole }) {
  const cls =
    role === 'admin'
      ? 'bg-[rgb(var(--color-bronze)/0.15)] text-[rgb(var(--color-bronze))]'
      : role === 'owner'
      ? 'bg-[rgb(var(--color-navy)/0.1)] text-[rgb(var(--color-navy)/0.8)]'
      : 'bg-[rgb(var(--color-navy)/0.05)] text-[rgb(var(--color-navy)/0.6)]';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-[var(--radius-sm)] font-mono text-[10px] uppercase tracking-wider ${cls}`}>
      {role === 'back_office' ? 'office' : role}
    </span>
  );
}

interface UserDrawerProps {
  user: AdminUser | null;
  isSelf: boolean;
  onClose: () => void;
  onChanged: () => void;
}

function UserDrawer({ user, isSelf, onClose, onChanged }: UserDrawerProps) {
  return (
    <Drawer open={!!user} onClose={onClose} width="w-[480px]">
      {user && <UserDetail user={user} isSelf={isSelf} onClose={onClose} onChanged={onChanged} />}
    </Drawer>
  );
}

function UserDetail({
  user,
  isSelf,
  onClose,
  onChanged,
}: {
  user: AdminUser;
  isSelf: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmForceSignOut, setConfirmForceSignOut] = useState(false);
  const [resentOk, setResentOk] = useState(false);
  const [forceSignOutOk, setForceSignOutOk] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [displayNameDraft, setDisplayNameDraft] = useState(user.displayName ?? '');
  // Reset the local draft whenever the underlying user changes (drawer
  // navigation between users, or onChanged refetch returning new data).
  useEffect(() => {
    setDisplayNameDraft(user.displayName ?? '');
  }, [user.userId, user.displayName]);

  const roleMutation = useMutation({
    mutationFn: (role: AppRole) => patchAdminUser(user.userId!, { role }),
    onSuccess: onChanged,
  });
  const activeMutation = useMutation({
    mutationFn: (active: boolean) => patchAdminUser(user.userId!, { active }),
    onSuccess: onChanged,
  });
  const displayNameMutation = useMutation({
    mutationFn: (displayName: string) =>
      patchAdminUser(user.userId!, { displayName }),
    onSuccess: onChanged,
  });

  function saveDisplayNameIfChanged() {
    const trimmed = displayNameDraft.trim();
    const current = user.displayName ?? '';
    if (trimmed === current) return; // no-op — avoids spurious PATCH
    displayNameMutation.mutate(trimmed);
  }
  const resendMutation = useMutation({
    mutationFn: () => resendAdminInvite(user.userId!),
    onSuccess: () => {
      setResentOk(true);
      window.setTimeout(() => setResentOk(false), 2000);
    },
  });
  const forceSignOutMutation = useMutation({
    mutationFn: () => forceSignOutUser(user.userId!),
    onSuccess: () => {
      setConfirmForceSignOut(false);
      setForceSignOutOk(true);
      window.setTimeout(() => setForceSignOutOk(false), 2000);
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminUser(user.userId!),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const recentLoginsQuery = useQuery({
    queryKey: ['admin', 'users', user.userId, 'recent-logins'],
    queryFn: () => getUserRecentLogins(user.userId!, 10),
    enabled: !!user.userId,
  });

  const mutating =
    roleMutation.isPending ||
    activeMutation.isPending ||
    displayNameMutation.isPending ||
    resendMutation.isPending ||
    forceSignOutMutation.isPending ||
    deleteMutation.isPending;

  async function handleCopyId() {
    if (!user.userId) return;
    try {
      await navigator.clipboard.writeText(user.userId);
      setCopiedId(true);
      window.setTimeout(() => setCopiedId(false), 1500);
    } catch {
      /* clipboard blocked — silently no-op */
    }
  }

  return (
    <>
      <header className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[rgb(var(--color-navy)/0.1)]">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-bronze))] font-semibold">
              User
            </span>
            {isSelf && (
              <span className="px-1.5 py-0.5 rounded-[var(--radius-sm)] bg-[rgb(var(--color-navy)/0.08)] text-[rgb(var(--color-navy)/0.7)] text-[9px] font-bold uppercase tracking-wider">
                You
              </span>
            )}
          </div>
          <h2 className="font-display text-xl text-[rgb(var(--color-navy))] break-all leading-tight">
            {user.displayName ?? user.email}
          </h2>
          {user.displayName && (
            <div className="text-[12px] text-[rgb(var(--color-navy)/0.6)] break-all mt-0.5">
              {user.email}
            </div>
          )}
          {user.userId && (
            <div className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.55)] mt-1">{user.userId.slice(0, 8)}…</div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close drawer"
          className="shrink-0 w-8 h-8 grid place-items-center rounded-[var(--radius-sm)] text-[rgb(var(--color-navy)/0.55)] hover:text-[rgb(var(--color-navy))] hover:bg-[rgb(var(--color-navy)/0.05)]"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* ── IDENTITY ─────────────────────────────────────────── */}
        <section className="space-y-3">
          <h3 className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold">
            Identity
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 bg-white/55 border border-[rgb(var(--color-navy)/0.12)] rounded-[var(--radius-md)] px-3 py-2.5">
              <label className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1 block">
                Display name
              </label>
              <input
                type="text"
                value={displayNameDraft}
                onChange={(e) => setDisplayNameDraft(e.target.value)}
                onBlur={saveDisplayNameIfChanged}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    (e.target as HTMLInputElement).blur();
                  }
                }}
                disabled={mutating || !user.userId}
                placeholder={user.email}
                maxLength={120}
                className="w-full h-8 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white px-2 text-sm focus:outline-none focus:border-[rgb(var(--color-bronze))] focus:ring-2 focus:ring-[rgb(var(--color-bronze)/0.2)] disabled:opacity-50"
              />
              <div className="text-[10px] text-[rgb(var(--color-navy)/0.5)] mt-1">
                {displayNameMutation.isPending
                  ? 'Saving…'
                  : displayNameMutation.error
                  ? (displayNameMutation.error instanceof Error
                      ? displayNameMutation.error.message
                      : 'Save failed')
                  : 'Falls back to email when blank. Saves on blur or Enter.'}
              </div>
            </div>
            <DetailCell label="Role">
              {isSelf ? (
                <RoleBadge role={user.role} />
              ) : (
                <select
                  value={user.role}
                  disabled={mutating}
                  onChange={(e) => roleMutation.mutate(e.target.value as AppRole)}
                  className="w-full h-8 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white px-2 text-sm disabled:opacity-50"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
              )}
              {roleMutation.error && (
                <div className="mt-1 text-[10px] text-[rgb(var(--color-severity-critical))]">
                  {roleMutation.error instanceof Error ? roleMutation.error.message : 'Save failed'}
                </div>
              )}
            </DetailCell>
            <DetailCell label="Status">
              {isSelf ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-[rgb(var(--color-navy)/0.85)]">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Active
                </span>
              ) : (
                <button
                  type="button"
                  disabled={mutating}
                  onClick={() => activeMutation.mutate(!user.active)}
                  className="inline-flex items-center gap-1.5 text-sm text-[rgb(var(--color-navy)/0.85)] hover:text-[rgb(var(--color-navy))] disabled:opacity-50"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${user.active ? 'bg-emerald-500' : 'bg-[rgb(var(--color-navy)/0.3)]'}`} />
                  {user.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                </button>
              )}
            </DetailCell>
            {user.userId && (
              <div className="col-span-2 bg-white/55 border border-[rgb(var(--color-navy)/0.12)] rounded-[var(--radius-md)] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1">
                  User ID
                </div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.85)] break-all flex-1">
                    {user.userId}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyId}
                    className="shrink-0 h-7 px-2 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white hover:bg-[rgb(var(--color-navy)/0.04)] text-[11px] text-[rgb(var(--color-navy))] font-medium"
                  >
                    {copiedId ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </div>
          {isSelf && (
            <p className="text-[11px] text-[rgb(var(--color-navy)/0.55)] italic">
              Your own role and status are locked — server blocks self-demote / self-deactivate. Have another admin make the change.
            </p>
          )}
        </section>

        {/* ── ACTIVITY ─────────────────────────────────────────── */}
        <section className="space-y-3">
          <h3 className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold">
            Activity
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <DetailCell label="Last sign-in">
              <span className="text-sm text-[rgb(var(--color-navy)/0.85)]">
                {user.lastSignInAt ? formatDate(user.lastSignInAt) : 'never'}
              </span>
              <div className="text-[11px] text-[rgb(var(--color-navy)/0.5)]">{formatRelative(user.lastSignInAt)}</div>
            </DetailCell>
            <DetailCell label="Created">
              <span className="text-sm text-[rgb(var(--color-navy)/0.85)]">{formatDate(user.createdAt)}</span>
              <div className="text-[11px] text-[rgb(var(--color-navy)/0.5)]">{formatRelative(user.createdAt)}</div>
            </DetailCell>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-2">
              Recent login attempts
            </div>
            <RecentLoginsList query={recentLoginsQuery} />
          </div>
        </section>

        {/* ── ACTIONS ─────────────────────────────────────────── */}
        <section className="space-y-2">
          <h3 className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-2">
            Actions
          </h3>
          <button
            type="button"
            disabled={mutating || !user.userId}
            onClick={() => resendMutation.mutate()}
            className="w-full h-9 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white/60 hover:bg-white text-sm text-[rgb(var(--color-navy))] disabled:opacity-50"
          >
            {resendMutation.isPending
              ? 'Sending…'
              : resentOk
              ? '✓ Sent'
              : 'Resend magic-link invite'}
          </button>
          {resendMutation.error && (
            <div className="text-[11px] text-[rgb(var(--color-severity-critical))]">
              {resendMutation.error instanceof Error ? resendMutation.error.message : 'Send failed'}
            </div>
          )}

          {!isSelf && (
            <>
              {/* Force sign-out — confirm-pattern (sign-out is reversible
                  but it WILL kick the user mid-session, so confirm once). */}
              {confirmForceSignOut ? (
                <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-bronze)/0.4)] bg-[rgb(var(--color-bronze)/0.06)] p-3">
                  <p className="text-sm text-[rgb(var(--color-navy))] mb-2">
                    Force sign-out <strong>{user.email}</strong>? They'll be kicked from all active sessions and must sign in again.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => forceSignOutMutation.mutate()}
                      className="flex-1 h-8 rounded-[var(--radius-sm)] bg-[rgb(var(--color-bronze))] text-white text-sm font-semibold hover:bg-[rgb(var(--color-bronze)/0.9)] disabled:opacity-50"
                    >
                      {forceSignOutMutation.isPending ? 'Signing out…' : 'Confirm force sign-out'}
                    </button>
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => setConfirmForceSignOut(false)}
                      className="h-8 px-3 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white text-sm text-[rgb(var(--color-navy))]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={mutating || !user.userId}
                  onClick={() => setConfirmForceSignOut(true)}
                  className="w-full h-9 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white/60 hover:bg-white text-sm text-[rgb(var(--color-navy))] disabled:opacity-50"
                >
                  {forceSignOutOk ? '✓ Sessions revoked' : 'Force sign-out (revoke sessions)'}
                </button>
              )}
              {forceSignOutMutation.error && (
                <div className="text-[11px] text-[rgb(var(--color-severity-critical))]">
                  {forceSignOutMutation.error instanceof Error ? forceSignOutMutation.error.message : 'Sign-out failed'}
                </div>
              )}

              {/* Delete user — destructive, confirm pattern */}
              {confirmDelete ? (
                <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-severity-critical)/0.3)] bg-[rgb(var(--color-severity-critical)/0.05)] p-3">
                  <p className="text-sm text-[rgb(var(--color-severity-critical))] mb-2">
                    Permanently delete <strong>{user.email}</strong>? This removes their auth.users row and cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => deleteMutation.mutate()}
                      className="flex-1 h-8 rounded-[var(--radius-sm)] bg-[rgb(var(--color-severity-critical))] text-white text-sm font-semibold hover:bg-[rgb(var(--color-severity-critical)/0.9)] disabled:opacity-50"
                    >
                      {deleteMutation.isPending ? 'Deleting…' : 'Confirm delete'}
                    </button>
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => setConfirmDelete(false)}
                      className="h-8 px-3 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white text-sm text-[rgb(var(--color-navy))]"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={mutating || !user.userId}
                  onClick={() => setConfirmDelete(true)}
                  className="w-full h-9 rounded-[var(--radius-sm)] border border-[rgb(var(--color-severity-critical)/0.3)] bg-white/60 hover:bg-[rgb(var(--color-severity-critical)/0.05)] text-sm text-[rgb(var(--color-severity-critical))] disabled:opacity-50"
                >
                  Delete user
                </button>
              )}
              {deleteMutation.error && (
                <div className="text-[11px] text-[rgb(var(--color-severity-critical))]">
                  {deleteMutation.error instanceof Error ? deleteMutation.error.message : 'Delete failed'}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
}

function RecentLoginsList({
  query,
}: {
  query: { data?: { events: UserRecentLogin[] }; isLoading: boolean; error: unknown };
}) {
  if (query.isLoading) {
    return (
      <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.1)] bg-white/40 p-3 text-[11px] text-[rgb(var(--color-navy)/0.5)]">
        Loading…
      </div>
    );
  }
  if (query.error) {
    return (
      <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-severity-critical)/0.3)] bg-[rgb(var(--color-severity-critical)/0.05)] p-3 text-[11px] text-[rgb(var(--color-severity-critical))]">
        {query.error instanceof Error ? query.error.message : 'Failed to load logins'}
      </div>
    );
  }
  const events = query.data?.events ?? [];
  if (events.length === 0) {
    return (
      <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.1)] bg-white/40 p-3 text-[11px] text-[rgb(var(--color-navy)/0.5)] italic">
        No recorded logins yet.
      </div>
    );
  }
  return (
    <div className="rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.1)] bg-white/40 overflow-hidden">
      <table className="w-full text-[11px]">
        <tbody>
          {events.map((e) => {
            const success = e.eventType === 'login_success';
            const browser = detectBrowserShort(e.userAgent);
            return (
              <tr
                key={e.id}
                className="border-b border-[rgb(var(--color-navy)/0.06)] last:border-b-0 align-top"
              >
                <td className="px-2 py-1.5 w-6 text-center">
                  <span
                    className={success ? 'text-emerald-600' : 'text-[rgb(var(--color-severity-critical))]'}
                    title={success ? 'Success' : 'Failed'}
                  >
                    {success ? '✓' : '✗'}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-[rgb(var(--color-navy)/0.7)] tabular-nums whitespace-nowrap">
                  {formatRelative(e.createdAt)}
                </td>
                <td className="px-2 py-1.5 text-[rgb(var(--color-navy)/0.6)] font-mono">
                  {e.country && e.country !== 'XX' ? e.country : '—'}
                </td>
                <td className="px-2 py-1.5 text-[rgb(var(--color-navy)/0.55)]" title={e.userAgent ?? ''}>
                  {browser ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function detectBrowserShort(ua: string | null): string | null {
  if (!ua) return null;
  if (/edg\//i.test(ua)) return 'Edge';
  if (/firefox/i.test(ua)) return 'Firefox';
  if (/chrome/i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return 'Safari';
  if (/curl|wget|python|httpclient|go-http/i.test(ua)) return 'CLI';
  if (/bot|crawler|spider/i.test(ua)) return 'Bot';
  return 'Other';
}

function DetailCell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-white/55 border border-[rgb(var(--color-navy)/0.12)] rounded-[var(--radius-md)] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function InviteDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('back_office');

  const mutation = useMutation({
    mutationFn: () => inviteAdminUser({ email: email.trim().toLowerCase(), role }),
    onSuccess: onInvited,
  });

  const canSubmit = email.trim().includes('@') && !mutation.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgb(var(--color-navy)/0.3)] backdrop-blur-[2px]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[28rem] rounded-[var(--radius-lg)] border border-[rgb(var(--color-navy)/0.15)] bg-[rgb(var(--color-linen))] p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-semibold text-[rgb(var(--color-navy))]">
          Invite a user
        </h2>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1 block">Email</label>
            <input
              type="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@company.com"
              disabled={mutation.isPending}
              className="w-full h-9 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white px-3 text-sm focus:outline-none focus:border-[rgb(var(--color-bronze))] focus:ring-2 focus:ring-[rgb(var(--color-bronze)/0.2)]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold mb-1 block">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as AppRole)}
              disabled={mutation.isPending}
              className="w-full h-9 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white px-3 text-sm"
            >
              <option value="back_office">Office staff</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {mutation.error && (
            <div className="rounded-[var(--radius-sm)] bg-[rgb(var(--color-severity-critical)/0.1)] px-3 py-2 text-xs text-[rgb(var(--color-severity-critical))]">
              {mutation.error instanceof Error ? mutation.error.message : 'Invite failed'}
            </div>
          )}
          {mutation.data?.warning && (
            <div className="rounded-[var(--radius-sm)] bg-amber-100 px-3 py-2 text-xs text-amber-900">
              {mutation.data.warning}
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-[var(--radius-sm)] border border-[rgb(var(--color-navy)/0.15)] bg-white text-sm text-[rgb(var(--color-navy))]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="h-9 px-4 rounded-[var(--radius-sm)] bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))] text-sm font-semibold hover:bg-[rgb(var(--color-navy)/0.9)] disabled:opacity-50"
          >
            {mutation.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
